import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EdHubDetailShell } from "../components/education/EdHubDetailShell";
import { LibraryCard } from "../components/education/LibraryCard";
import { useAnalytics } from "../lib/analytics";
import { readFavorites, toggleFavorite } from "../lib/edhub/favorites";
import { filterByLocale } from "../lib/edhub/localeFilter";
import { useClientTranslations } from "../lib/i18n/client";
import {
	type EducationContent,
	fetchLibraryContent,
} from "../services/educationService";
import styles from "./EducationLibrary.module.css";

const FILTER_OPTIONS = [
	{ key: "all", labelKey: "library.filterAll", fallback: "All" },
	{
		key: "ivf-treatments",
		labelKey: "library.filterIvf",
		fallback: "IVF & Treatments",
	},
	{
		key: "mental-health",
		labelKey: "library.filterMental",
		fallback: "Mental Health",
	},
	{
		key: "nutrition",
		labelKey: "library.filterNutrition",
		fallback: "Nutrition",
	},
];

/**
 * Library page (Figma 2420:25573). Videos + podcasts only. Filter chips are
 * visual-only (no real filtering until content classification ships); the
 * ?category= param just preselects a chip. Favorites persist in localStorage.
 */
export function EducationLibrary() {
	const { t, locale } = useClientTranslations("education");
	const navigate = useNavigate();
	const { logEducation } = useAnalytics();
	const [params] = useSearchParams();
	const [items, setItems] = useState<EducationContent[]>([]);
	const [favorites, setFavorites] = useState<Set<string>>(() =>
		readFavorites(),
	);
	const [filter, setFilter] = useState(params.get("category") ?? "all");

	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (hasTrackedPageView.current) return;
		hasTrackedPageView.current = true;
		logEducation("library_page_viewed");
	}, [logEducation]);

	useEffect(() => {
		let cancelled = false;
		fetchLibraryContent()
			.then((rows) => {
				if (!cancelled) setItems(rows);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, []);

	// Category filtering is deferred to the classification plan — chips are
	// visual-only. Language: show only the items matching the app locale.
	const filtered = useMemo(
		() => filterByLocale(items, locale),
		[items, locale],
	);

	const handleToggleFavorite = (id: string) => {
		// favorites is a Set of currently-favorited ids; the toggle below flips it.
		logEducation("library_favorite_toggled", {
			contentId: id,
			favorited: !favorites.has(id),
		});
		toggleFavorite(id);
		setFavorites(readFavorites());
	};

	return (
		<EdHubDetailShell
			onClose={() => navigate("/education")}
			closeLabel={t("library.close", "Close library")}
		>
			<div className={styles.main}>
				<header className={styles.heading}>
					<h1 className={styles.title}>
						{t("library.title", "Customized Library")}
					</h1>
					<p className={styles.subtitle}>
						{t(
							"library.subtitle",
							"All the information and guidance you need for your journey.",
						)}
					</p>
				</header>

				<div className={styles.filterRow}>
					{FILTER_OPTIONS.map((f) => (
						<button
							key={f.key}
							type="button"
							className={`${styles.chip} ${filter === f.key ? styles.chipActive : ""}`}
							onClick={() => {
								setFilter(f.key);
								logEducation("library_filter_selected", { filter: f.key });
							}}
						>
							{t(f.labelKey, f.fallback)}
						</button>
					))}
				</div>

				{filtered.length === 0 ? (
					<p className={styles.empty}>
						{t("library.empty", "No videos or podcasts yet.")}
					</p>
				) : (
					<div className={styles.grid}>
						{filtered.map((c) => (
							<LibraryCard
								key={c.id}
								item={c}
								favorited={favorites.has(c.id)}
								onToggleFavorite={handleToggleFavorite}
								onOpen={(item) =>
									logEducation("library_card_opened", {
										contentId: item.id,
										contentType: item.contentType,
									})
								}
							/>
						))}
					</div>
				)}
			</div>
		</EdHubDetailShell>
	);
}
